import React from 'react';
import CollectorCard from './passport/CollectorCard';
import { vehicleSourceCard } from './passport/sourceCards';
import type { IssuedBadge } from '../types/discoveryBadges';

export interface VehicleCardProps {
  vehicle: {
    card_projection?: import('./passport/sourceCards').VehicleSource['card_projection'];
    id: number;
    year: number;
    make: string;
    model: string;
    trim: string;
    color?: string;
    exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
    exterior_finish_color?: string | null;
    exterior_finish_brand?: string | null;
    exterior_finish_style?: string | null;
    exterior_finish_label?: string | null;
    nickname?: string;
    description?: string;
    modification_count?: number;
    last_modification?: string;
    display_name: string;
    owner_username?: string;
    owner_display_name?: string;
    image_url?: string;
    focal_x?: number | null;
    focal_y?: number | null;
    zoom?: number | null;
    is_primary?: boolean;
    is_public?: boolean;
    created_at: string;
    updated_at?: string;
    chassis?: string | null;
    engine?: string | null;
    drivetrain?: string | null;
    horsepower?: number;
    horsepower_measurement?: 'wheel' | 'crank' | null;
    torque?: number;
    redline?: number;
    settings?: {
      show_pricing?: boolean;
      show_performance?: boolean;
    };
    total_investment?: number;
    like_count?: number;
    user_has_liked?: boolean;
    favorite_count?: number;
    is_favorited?: boolean;
    view_count?: number;
    pp?: number;
    rarity_stars?: number;
    collector_points?: number;
    base_rarity_stars?: number;
    base_collector_points?: number;
    base_rarity_level?: string;
    vehicle_overview?: string;
    production_years?: string;
    production_numbers?: string;
    /** Human-reviewed build truth — see docs/BUILD_CARD_AND_CERTIFICATION.md */
    certification_status?: string | null;
  };
  onVehicleClick: (vehicle: any) => void;
  href?: string;
  children?: React.ReactNode;
  onUserClick?: (username: string) => void;
  showOwner?: boolean;
  isOwner?: boolean;
  onEditClick?: (vehicle: any) => void;
  completionPercent?: number;
  /** Garage / collection view — calmer card, photo-first (tier frames still apply). */
  collectionMode?: boolean;
  /** Legacy prop retained for callers; all build cards now show both engagement controls. */
  showFavorite?: boolean;
  /** Like control; default same as favorite visibility. */
  showLike?: boolean;
  onFavoriteChange?: (favorited: boolean, favoriteCount: number) => void;
  onLikeChange?: (liked: boolean, likeCount: number) => void;
  discoveryBadges?: IssuedBadge[];
  discoveryBadgeOverflow?: number;
  onBadgeOverflowClick?: () => void;
  limitedDesignationPrimary?: {
    public_id: string;
    name: string;
    ordinal: number;
    edition_size: number;
    ordinal_label?: string;
    issuer_label?: string;
    public_blurb?: string | null;
    issued_at?: string;
  } | null;
}

/** Shared source card with existing owner and engagement contracts preserved. */
const VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  onVehicleClick,
  href,
  onUserClick,
  isOwner = false,
  children,
  completionPercent,
  onEditClick,
  onFavoriteChange,
  onLikeChange,
  discoveryBadges,
  discoveryBadgeOverflow = 0,
  onBadgeOverflowClick,
  limitedDesignationPrimary,
}) => {
  const card = vehicleSourceCard(vehicle, isOwner);
  if (card.subject)
    card.subject.card_summary = {
      ...card.subject.card_summary,
      badges: card.subject.card_summary?.badges ?? discoveryBadges,
      badge_overflow: card.subject.card_summary?.badge_overflow ?? discoveryBadgeOverflow,
      limited_designation:
        limitedDesignationPrimary ?? card.subject.card_summary?.limited_designation,
    };
  return (
    <CollectorCard
      card={card}
      href={href}
      onOpen={() => onVehicleClick(vehicle)}
      openLabel="View build"
      onOwner={
        onUserClick && vehicle.owner_username
          ? () => onUserClick(vehicle.owner_username!)
          : undefined
      }
      onRecognition={onBadgeOverflowClick}
      fullRecordExtension={
        children || isOwner ? (
          <section>
            {isOwner && vehicle.is_public === false && <p>Private build</p>}
            {isOwner && completionPercent != null && (
              <p>Build profile completion: {completionPercent}%</p>
            )}
            {children}
          </section>
        ) : undefined
      }
      actions={{
        vehicle: {
          id: vehicle.id,
          ownerUsername: vehicle.owner_username,
          like: {
            count: vehicle.like_count ?? Number(card.subject?.facts?.like_count ?? 0),
            selected: vehicle.user_has_liked ?? Number(card.subject?.facts?.user_has_liked) === 1,
            onChange: onLikeChange,
          },
          favorite: {
            count: vehicle.favorite_count ?? Number(card.subject?.facts?.favorite_count ?? 0),
            selected: vehicle.is_favorited ?? Number(card.subject?.facts?.is_favorited) === 1,
            onChange: onFavoriteChange,
          },
        },
        edit: isOwner && onEditClick ? () => onEditClick(vehicle) : undefined,
      }}
    />
  );
};
export default VehicleCard;
